import { Drawer, Layout, Menu, Tooltip, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { createElement } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useUiStore } from '@/store/ui';
import { navGroups } from '@/app/nav-config';
import { useSchoolTheme } from '@/themes/use-theme';
import { branding } from '@/config/branding';
import { BrandMark } from '@/shared/components/brand-mark';
import { useAuthStore } from '@/store/auth';
import { useAbility } from '@/auth/ability-provider';
import { useIsMobile } from '@/shared/hooks/use-media';
import { YearPicker } from '@/app/year-picker';
import { ThemePicker } from '@/app/theme-picker';

const ACADEMIC_YEAR_LABELS: Record<number, string> = {
  1: '2024–25',
  2: '2025–26',
  3: '2026–27',
};

type MenuItem = NonNullable<MenuProps['items']>[number];

interface SiderBodyProps {
  collapsed: boolean;
  onNavigate?: () => void;
  showFooterToggle: boolean;
  showMobileExtras?: boolean;
}

function SiderBody({ collapsed, onNavigate, showFooterToggle, showMobileExtras }: SiderBodyProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const toggle = useUiStore((s) => s.toggleSider);

  const ability = useAbility();
  const allowItem = (item: (typeof navGroups)[number]['items'][number]) => {
    if (!item.can) return true;
    return ability.can(item.can.action, item.can.subject);
  };

  const visibleGroups = navGroups
    .map((g) => ({ group: g.group, items: g.items.filter(allowItem) }))
    .filter((g) => g.items.length > 0);

  const selectedKey = visibleGroups
    .flatMap((g) => g.items)
    .find((i) => location.pathname === i.path || location.pathname.startsWith(i.path + '/'))
    ?.key ?? 'dashboard';

  const menuItems: MenuItem[] = visibleGroups.flatMap((g, idx): MenuItem[] => {
    const itemNodes: MenuItem[] = g.items.map((item) => ({
      key: item.key,
      icon: createElement(item.icon),
      label: item.label,
      onClick: () => {
        navigate(item.path);
        onNavigate?.();
      },
    }));
    if (g.group == null) return itemNodes;
    return [
      {
        type: 'group',
        key: `group-${idx}`,
        label: collapsed ? '' : (
          <span style={{ fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase', opacity: 0.55 }}>
            {g.group}
          </span>
        ),
        children: itemNodes,
      },
    ];
  });

  const academicYearId = useAuthStore((s) => s.academicYearId) ?? 2;
  const yearLabel = ACADEMIC_YEAR_LABELS[academicYearId] ?? '';
  const subtitle = [branding.locality, yearLabel].filter(Boolean).join(' · ');

  return (
    <div className="sider-shell">
      <div className="sider-brand" data-collapsed={collapsed}>
        <BrandMark />
        <div className="brand-text" aria-hidden={collapsed}>
          <Typography.Text strong style={{ color: '#fff', fontSize: 15, display: 'block', lineHeight: 1.2 }}>
            {branding.shortName}
          </Typography.Text>
          {subtitle && (
            <Typography.Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11 }}>
              {subtitle}
            </Typography.Text>
          )}
        </div>
      </div>

      <div className="sider-menu-wrap">
        <Menu
          mode="inline"
          theme="dark"
          selectedKeys={[selectedKey]}
          items={menuItems}
          inlineIndent={20}
          style={{ borderInlineEnd: 'none', background: 'transparent' }}
        />
      </div>

      {showMobileExtras && (
        <div className="sider-mobile-extras">
          <YearPicker />
          <ThemePicker />
        </div>
      )}

      {showFooterToggle && (
        <div className="sider-footer" data-collapsed={collapsed}>
          <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
            <button
              type="button"
              className="sider-footer-btn"
              aria-label="Toggle sidebar"
              onClick={toggle}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </button>
          </Tooltip>
          <Tooltip title="Help & shortcuts" placement="top">
            <button
              type="button"
              className="sider-footer-btn sider-footer-aux"
              aria-label="Help"
              tabIndex={collapsed ? -1 : 0}
              aria-hidden={collapsed}
            >
              <QuestionCircleOutlined />
            </button>
          </Tooltip>
          <span className="sider-version" aria-hidden={collapsed}>v0.1.0</span>
        </div>
      )}
    </div>
  );
}

export function AppSider() {
  const collapsed = useUiStore((s) => s.siderCollapsed);
  const mobileOpen = useUiStore((s) => s.mobileMenuOpen);
  const closeMobile = useUiStore((s) => s.closeMobileMenu);
  const theme = useSchoolTheme();
  const isMobile = useIsMobile();

  const primary = (theme.config.token?.colorPrimary as string) ?? '#5b9bff';
  const secondary = theme.swatch[2];
  const cssVars = {
    ['--brand-primary' as string]: primary,
    ['--brand-secondary' as string]: secondary,
  };

  if (isMobile) {
    return (
      <Drawer
        open={mobileOpen}
        onClose={closeMobile}
        placement="left"
        size={Math.min(288, typeof window !== 'undefined' ? window.innerWidth - 56 : 288)}
        styles={{
          body: { padding: 0, background: '#001529', ...cssVars },
          header: { display: 'none' },
        }}
        rootClassName="mobile-nav-drawer"
      >
        <SiderBody collapsed={false} onNavigate={closeMobile} showFooterToggle={false} showMobileExtras />
      </Drawer>
    );
  }

  return (
    <Layout.Sider
      collapsible
      collapsed={collapsed}
      trigger={null}
      width={244}
      collapsedWidth={76}
      theme="dark"
      style={{
        position: 'sticky',
        top: 0,
        height: '100vh',
        ...cssVars,
      }}
    >
      <SiderBody collapsed={collapsed} showFooterToggle />
    </Layout.Sider>
  );
}
